import React, { useMemo, useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday, parseISO, isWeekend } from 'date-fns';

export interface CalendarData {
    date: string;
    day: number;
    present: number;
    absent: number;
    late: number;
    leave: number;
    missed: number;
    expected_records: number;
    total: number;
    percentage: number;
    isHoliday: boolean;
    holidayName: string | null;
    isOffDay: boolean; // Weekend or off-day
}

export interface MonthlyAttendanceCalendarProps {
    data: CalendarData[];
    year: number;
    month: number; // 1-12
    isLoading: boolean;
    highlightDate?: string; // YYYY-MM-DD
    onDayClick?: (date: string) => void;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function MonthlyAttendanceCalendar({
    data,
    year,
    month,
    isLoading,
    highlightDate,
    onDayClick
}: MonthlyAttendanceCalendarProps) {
    const currentDate = useMemo(() => new Date(year, month - 1, 1), [year, month]);
    const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
    const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
    const daysInMonth = useMemo(() => eachDayOfInterval({ start: monthStart, end: monthEnd }), [monthStart, monthEnd]);

    const dateMap = useMemo(() => {
        const map = new Map<string, CalendarData>();
        data.forEach(d => map.set(d.date, {
            ...d,
            present: d.present ?? 0,
            absent: d.absent ?? 0,
            late: d.late ?? 0,
            leave: d.leave ?? 0,
            missed: d.missed ?? 0,
            total: d.total ?? 0,
            expected_records: d.expected_records ?? 0,
            percentage: d.percentage ?? 0,
        }));
        return map;
    }, [data]);

    // Active selected date state (defaults to highlightDate, today if in current month, latest recorded day, or 1st of month)
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const monthPrefix = format(currentDate, 'yyyy-MM');
        if (highlightDate && highlightDate.startsWith(monthPrefix)) {
            return highlightDate;
        }
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        if (todayStr.startsWith(monthPrefix)) {
            return todayStr;
        }
        const dayWithData = [...data].reverse().find(d => (d.total > 0 || d.present > 0 || d.absent > 0));
        if (dayWithData && dayWithData.date.startsWith(monthPrefix)) {
            return dayWithData.date;
        }
        return format(monthStart, 'yyyy-MM-dd');
    });

    // Keep selectedDate in sync when highlightDate or month/year changes
    useEffect(() => {
        const monthPrefix = format(currentDate, 'yyyy-MM');
        if (highlightDate && highlightDate.startsWith(monthPrefix)) {
            setSelectedDate(highlightDate);
            return;
        }
        // If current selectedDate is outside the displayed month, adjust it
        if (!selectedDate.startsWith(monthPrefix)) {
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            if (todayStr.startsWith(monthPrefix)) {
                setSelectedDate(todayStr);
            } else {
                const dayWithData = [...data].reverse().find(d => (d.total > 0 || d.present > 0 || d.absent > 0));
                if (dayWithData && dayWithData.date.startsWith(monthPrefix)) {
                    setSelectedDate(dayWithData.date);
                } else {
                    setSelectedDate(format(monthStart, 'yyyy-MM-dd'));
                }
            }
        }
    }, [highlightDate, currentDate, monthStart, data, selectedDate]);

    // Month summary metrics calculation
    const monthMetrics = useMemo(() => {
        let recordedDaysCount = 0;
        let totalPctSum = 0;
        let schoolDaysCount = 0;
        let maxExpected = 0;

        daysInMonth.forEach(date => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const d = dateMap.get(dateStr);
            const isOff = d ? d.isOffDay : isWeekend(date);
            const isHol = d ? d.isHoliday : false;

            if (!isOff && !isHol) {
                schoolDaysCount++;
            }

            if (d && (d.total > 0 || d.present > 0 || d.absent > 0 || d.late > 0 || d.leave > 0)) {
                recordedDaysCount++;
                totalPctSum += d.percentage;
                if (d.expected_records > maxExpected) maxExpected = d.expected_records;
                if (d.total > maxExpected) maxExpected = d.total;
            }
        });

        const avgRate = recordedDaysCount > 0 ? (totalPctSum / recordedDaysCount).toFixed(1) : '—';
        return {
            avgRate,
            recordedDaysCount,
            schoolDaysCount,
            maxExpected,
        };
    }, [daysInMonth, dateMap]);

    // Calculate empty slots for start of month grid
    const startDayOfWeek = getDay(monthStart); // 0 (Sun) to 6 (Sat)
    const emptySlots = Array.from({ length: startDayOfWeek });

    // Active selected day data object
    const selectedDateObj = useMemo(() => {
        try {
            return parseISO(selectedDate);
        } catch {
            return new Date();
        }
    }, [selectedDate]);

    const activeDayData: CalendarData = useMemo(() => {
        const found = dateMap.get(selectedDate);
        if (found) return found;
        const dObj = selectedDateObj;
        return {
            date: selectedDate,
            day: parseInt(format(dObj, 'd'), 10) || 1,
            present: 0,
            absent: 0,
            late: 0,
            leave: 0,
            missed: 0,
            total: 0,
            expected_records: 0,
            percentage: 0,
            isHoliday: false,
            holidayName: null,
            isOffDay: isWeekend(dObj),
        };
    }, [dateMap, selectedDate, selectedDateObj]);

    const isSelectedHoliday = !!activeDayData.isHoliday;
    const isSelectedOffDay = activeDayData.isOffDay;
    const isSelectedHasData = activeDayData.total > 0 || activeDayData.present > 0 || activeDayData.absent > 0 || activeDayData.late > 0 || activeDayData.leave > 0;
    const isSelectedToday = isToday(selectedDateObj);

    const denom = activeDayData.expected_records > 0
        ? activeDayData.expected_records
        : (activeDayData.total > 0
            ? activeDayData.total
            : (activeDayData.present + activeDayData.absent + activeDayData.late + activeDayData.leave + (activeDayData.missed || 0)));

    const presentPct = denom > 0 ? ((activeDayData.present / denom) * 100).toFixed(1) : '0.0';
    const absentPct = denom > 0 ? ((activeDayData.absent / denom) * 100).toFixed(1) : '0.0';
    const latePct = denom > 0 ? ((activeDayData.late / denom) * 100).toFixed(1) : '0.0';
    const leavePct = denom > 0 ? ((activeDayData.leave / denom) * 100).toFixed(1) : '0.0';
    const missedPct = denom > 0 ? (((activeDayData.missed || 0) / denom) * 100).toFixed(1) : '0.0';
    const totalRecorded = activeDayData.present + activeDayData.absent + activeDayData.late + activeDayData.leave;

    const handleSelectDay = (dateStr: string) => {
        setSelectedDate(dateStr);
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="monthly-attendance-calendar">
            {/* Card Chrome Header */}
            <div className="p-4 sm:px-6 py-3.5 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                    <div>
                        <h3 className="font-bold text-slate-800 text-sm sm:text-base">Monthly Attendance Overview</h3>
                        <p className="text-xs text-slate-500">Visual Attendance Density & Heatmap Grid</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-3 py-1 bg-white border border-slate-200 rounded-lg text-slate-700 shadow-xs">
                        {format(currentDate, 'MMMM yyyy')}
                    </span>
                </div>
            </div>

            {isLoading ? (
                <div className="p-12 text-center text-slate-400">Loading calendar data...</div>
            ) : (
                <div className="p-3.5 sm:p-5 space-y-4">
                    {/* Month Summary Metrics */}
                    {monthMetrics.recordedDaysCount > 0 && (
                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                            <div className="bg-slate-50/80 p-2 sm:p-2.5 rounded-xl border border-slate-200/80 text-center">
                                <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Avg Rate</div>
                                <div className="text-sm sm:text-base font-extrabold text-emerald-600 mt-0.5">
                                    {monthMetrics.avgRate}{monthMetrics.avgRate !== '—' ? '%' : ''}
                                </div>
                            </div>
                            <div className="bg-slate-50/80 p-2 sm:p-2.5 rounded-xl border border-slate-200/80 text-center">
                                <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">School Days</div>
                                <div className="text-sm sm:text-base font-extrabold text-slate-800 mt-0.5">
                                    {monthMetrics.recordedDaysCount}/{monthMetrics.schoolDaysCount}
                                </div>
                            </div>
                            <div className="bg-slate-50/80 p-2 sm:p-2.5 rounded-xl border border-slate-200/80 text-center">
                                <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Expected Roster</div>
                                <div className="text-sm sm:text-base font-extrabold text-slate-800 mt-0.5">
                                    {monthMetrics.maxExpected > 0 ? monthMetrics.maxExpected : '—'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Main Layout: Heatmap on Left/Top + Side Inspector on Right/Bottom */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
                        {/* Heatmap Section */}
                        <div className="lg:col-span-7 xl:col-span-7 space-y-3">
                            {/* Weekday Column Headers */}
                            <div className="grid grid-cols-7 text-center">
                                {WEEKDAYS.map(day => (
                                    <div key={day} className="text-center text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider py-1">
                                        <span className="sm:hidden">{day.slice(0, 1)}</span>
                                        <span className="hidden sm:inline">{day.slice(0, 3)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* 7-Col Heatmap Grid */}
                            <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                                {emptySlots.map((_, i) => (
                                    <div key={`empty-${i}`} className="aspect-square sm:aspect-auto sm:h-14 lg:h-16 bg-slate-50/40 rounded-lg sm:rounded-xl border border-transparent" />
                                ))}

                                {daysInMonth.map(date => {
                                    const dateStr = format(date, 'yyyy-MM-dd');
                                    const dayData = dateMap.get(dateStr);
                                    const isTodayDate = isToday(date);
                                    const isSelected = selectedDate === dateStr;

                                    const isHoliday = !!dayData?.isHoliday;
                                    const isOffDay = dayData ? dayData.isOffDay : isWeekend(date);
                                    const hasData = dayData && (dayData.total > 0 || dayData.present > 0 || dayData.absent > 0 || dayData.late > 0 || dayData.leave > 0);
                                    const pct = dayData ? dayData.percentage : 0;

                                    let colorClass = "bg-slate-50 text-slate-300 border border-dashed border-slate-200";
                                    if (isHoliday) {
                                        colorClass = "bg-purple-100 text-purple-900 font-bold border border-purple-300";
                                    } else if (isOffDay) {
                                        colorClass = "bg-slate-100 text-slate-400 border border-slate-200";
                                    } else if (hasData) {
                                        if (pct >= 92) {
                                            colorClass = "bg-emerald-600 text-white font-bold border border-emerald-700 shadow-xs";
                                        } else if (pct >= 85) {
                                            colorClass = "bg-emerald-300 text-emerald-950 font-bold border border-emerald-400";
                                        } else if (pct >= 75) {
                                            colorClass = "bg-amber-300 text-amber-950 font-bold border border-amber-400";
                                        } else {
                                            colorClass = "bg-rose-400 text-white font-bold border border-rose-500 shadow-xs";
                                        }
                                    }

                                    let ringClass = "";
                                    if (isSelected) {
                                        ringClass = "ring-3 ring-sky-600 ring-offset-2 z-10 scale-[1.04] shadow-md";
                                    } else if (isTodayDate) {
                                        ringClass = "ring-2 ring-sky-400 ring-offset-1";
                                    }

                                    return (
                                        <button
                                            key={dateStr}
                                            type="button"
                                            onClick={() => handleSelectDay(dateStr)}
                                            className={`aspect-square sm:aspect-auto sm:h-14 lg:h-16 rounded-lg sm:rounded-xl p-1 sm:p-2 flex flex-col items-center justify-center sm:justify-between sm:items-start transition-all cursor-pointer select-none relative ${colorClass} ${ringClass} hover:opacity-95 active:scale-95`}
                                            title={`${format(date, 'EEEE, MMMM d, yyyy')}${hasData ? ` - ${pct}% attendance` : isHoliday ? ` - Holiday (${dayData?.holidayName || 'Holiday'})` : isOffDay ? ' - Off Day' : ' - No Data'}`}
                                        >
                                            {/* Day number & desktop tag */}
                                            <div className="flex sm:justify-between sm:w-full items-center">
                                                <span className="text-xs sm:text-sm font-bold leading-none">{format(date, 'd')}</span>
                                                <span className="hidden sm:inline text-[9px] uppercase opacity-75 leading-none">
                                                    {isHoliday ? 'Hol' : isOffDay ? 'Off' : ''}
                                                </span>
                                            </div>

                                            {/* Value / status label */}
                                            <div className="text-center sm:text-right sm:w-full mt-0.5 sm:mt-auto">
                                                {isHoliday ? (
                                                    <span className="text-[8px] sm:text-[10px] font-semibold sm:font-bold leading-none block truncate max-w-full">
                                                        <span className="sm:hidden">Hol</span>
                                                        <span className="hidden sm:inline">Holiday</span>
                                                    </span>
                                                ) : isOffDay ? (
                                                    <span className="text-[8px] sm:text-[10px] font-medium opacity-80 leading-none block">
                                                        Off
                                                    </span>
                                                ) : hasData ? (
                                                    <span className="text-[9px] sm:text-xs font-black leading-none block">
                                                        {Math.round(pct)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-[8px] sm:text-[10px] opacity-40 leading-none block">
                                                        —
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Legend */}
                            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-1.5 text-[10px] sm:text-xs text-slate-500 font-medium">
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-emerald-600 shrink-0"></span><span>&gt;92%</span></div>
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-emerald-300 border border-emerald-400 shrink-0"></span><span>85–91%</span></div>
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-amber-300 border border-amber-400 shrink-0"></span><span>75–84%</span></div>
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-rose-400 shrink-0"></span><span>&lt;75%</span></div>
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-purple-100 border border-purple-300 shrink-0"></span><span>Holiday</span></div>
                                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-slate-100 border border-slate-200 shrink-0"></span><span>Off</span></div>
                            </div>
                        </div>

                        {/* Inline Breakdown Card / Side Inspector */}
                        <div className="lg:col-span-5 xl:col-span-5 flex flex-col">
                            <div className="bg-slate-50/70 rounded-2xl border border-slate-200/80 p-3.5 sm:p-4 flex flex-col justify-between h-full space-y-3.5">
                                {/* Inspector Header */}
                                <div className="flex items-start justify-between border-b border-slate-200/70 pb-2.5">
                                    <div>
                                        <div className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider">
                                            {format(selectedDateObj, 'EEEE')}
                                            {isSelectedToday && (
                                                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-bold">Today</span>
                                            )}
                                        </div>
                                        <h4 className="text-sm sm:text-base font-bold text-slate-800 mt-0.5">
                                            {format(selectedDateObj, 'MMMM d, yyyy')}
                                        </h4>
                                    </div>
                                    <div>
                                        {isSelectedHoliday ? (
                                            <span className="px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs font-bold">
                                                Holiday
                                            </span>
                                        ) : isSelectedOffDay ? (
                                            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-xs font-bold">
                                                Off Day
                                            </span>
                                        ) : isSelectedHasData ? (
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border shadow-xs ${
                                                activeDayData.percentage >= 90
                                                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                                    : activeDayData.percentage >= 75
                                                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                                                    : 'bg-rose-100 text-rose-800 border-rose-200'
                                            }`}>
                                                {activeDayData.percentage}% Rate
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-400 border border-slate-200 text-xs font-medium">
                                                No Records
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Inspector Content */}
                                {isSelectedHoliday ? (
                                    <div className="text-center py-6 px-4 bg-purple-50 rounded-xl border border-purple-100 my-auto">
                                        <div className="w-9 h-9 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center mx-auto mb-2 text-base font-bold">
                                            🎉
                                        </div>
                                        <div className="font-bold text-sm text-purple-900">
                                            {activeDayData.holidayName || 'Official School Holiday'}
                                        </div>
                                        <p className="mt-1 text-xs text-purple-700/80">
                                            Gazetted holiday observance across all classes.
                                        </p>
                                    </div>
                                ) : isSelectedOffDay ? (
                                    <div className="text-center py-6 px-4 bg-slate-100/70 rounded-xl border border-slate-200/80 my-auto">
                                        <div className="w-9 h-9 bg-slate-200/70 text-slate-500 rounded-full flex items-center justify-center mx-auto mb-2 text-base">
                                            🏖️
                                        </div>
                                        <div className="font-bold text-sm text-slate-700">Weekly Off Day</div>
                                        <p className="mt-1 text-xs text-slate-500">
                                            Regular weekend non-instructional day.
                                        </p>
                                    </div>
                                ) : !isSelectedHasData ? (
                                    <div className="text-center py-6 px-4 bg-slate-100/50 rounded-xl border border-dashed border-slate-200 my-auto">
                                        <div className="w-9 h-9 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-2 text-base">
                                            📅
                                        </div>
                                        <div className="font-bold text-sm text-slate-600">Upcoming / Unmarked Session</div>
                                        <p className="mt-1 text-xs text-slate-400">
                                            No attendance records submitted for this date yet.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {/* 4 Stats Cards */}
                                        <div className="grid grid-cols-2 gap-2 text-center text-xs">
                                            <div className="bg-emerald-50/90 p-2 sm:p-2.5 rounded-xl border border-emerald-100">
                                                <div className="text-[10px] uppercase font-bold text-emerald-800">Present</div>
                                                <div className="text-base font-black text-emerald-700">{activeDayData.present}</div>
                                                <div className="text-[10px] text-emerald-600 font-semibold">{presentPct}%</div>
                                            </div>
                                            <div className="bg-rose-50/90 p-2 sm:p-2.5 rounded-xl border border-rose-100">
                                                <div className="text-[10px] uppercase font-bold text-rose-800">Absent</div>
                                                <div className="text-base font-black text-rose-700">{activeDayData.absent}</div>
                                                <div className="text-[10px] text-rose-600 font-semibold">{absentPct}%</div>
                                            </div>
                                            <div className="bg-amber-50/90 p-2 sm:p-2.5 rounded-xl border border-amber-100">
                                                <div className="text-[10px] uppercase font-bold text-amber-800">Late</div>
                                                <div className="text-base font-black text-amber-700">{activeDayData.late}</div>
                                                <div className="text-[10px] text-amber-600 font-semibold">{latePct}%</div>
                                            </div>
                                            <div className="bg-blue-50/90 p-2 sm:p-2.5 rounded-xl border border-blue-100">
                                                <div className="text-[10px] uppercase font-bold text-blue-800">Leave</div>
                                                <div className="text-base font-black text-blue-700">{activeDayData.leave}</div>
                                                <div className="text-[10px] text-blue-600 font-semibold">{leavePct}%</div>
                                            </div>
                                        </div>

                                        {/* Unmarked Notice if any */}
                                        {(activeDayData.missed || 0) > 0 && (
                                            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-center justify-between font-semibold">
                                                <span className="flex items-center gap-1">
                                                    <span>⚡</span>
                                                    <span>Unmarked / In-Progress:</span>
                                                </span>
                                                <span className="font-black text-amber-800">
                                                    {activeDayData.missed} ({missedPct}%)
                                                </span>
                                            </div>
                                        )}

                                        {/* Stacked Composition Bar */}
                                        <div>
                                            <div className="flex justify-between text-[11px] text-slate-500 mb-1 font-medium">
                                                <span>Roster Composition</span>
                                                <span>{totalRecorded} / {denom} Records</span>
                                            </div>
                                            <div className="w-full bg-slate-200 rounded-full h-2 flex overflow-hidden shadow-inner">
                                                {activeDayData.present > 0 && (
                                                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(activeDayData.present / denom) * 100}%` }} title={`Present: ${activeDayData.present}`} />
                                                )}
                                                {activeDayData.absent > 0 && (
                                                    <div className="h-full bg-rose-500 transition-all" style={{ width: `${(activeDayData.absent / denom) * 100}%` }} title={`Absent: ${activeDayData.absent}`} />
                                                )}
                                                {activeDayData.late > 0 && (
                                                    <div className="h-full bg-amber-500 transition-all" style={{ width: `${(activeDayData.late / denom) * 100}%` }} title={`Late: ${activeDayData.late}`} />
                                                )}
                                                {activeDayData.leave > 0 && (
                                                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${(activeDayData.leave / denom) * 100}%` }} title={`Leave: ${activeDayData.leave}`} />
                                                )}
                                                {(activeDayData.missed || 0) > 0 && (
                                                    <div className="h-full bg-slate-400 transition-all" style={{ width: `${((activeDayData.missed || 0) / denom) * 100}%` }} title={`Unmarked: ${activeDayData.missed}`} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Inspector Footer */}
                                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs text-slate-500">
                                    <span className="text-[11px]">
                                        {onDayClick ? 'Tap any tile to inspect' : 'Selected day view'}
                                    </span>
                                    {onDayClick && (
                                        <button
                                            type="button"
                                            onClick={() => onDayClick(selectedDate)}
                                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-xs"
                                        >
                                            Day Details →
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

